/**
 * Vertical top-to-bottom layout for approval workflow nodes.
 * Maps design JSON (stages[]) into React Flow nodes/edges with automatic positioning.
 */
import {
  kindToNodeType,
  nodeTypeToDefaultKind,
  approverDisplayLabel,
} from './approvalFlowConvert';

export { flowToDesign, kindToNodeType, nodeTypeToDefaultKind } from './approvalFlowConvert';

const NODE_W = 220;
const NODE_H = 72;
const V_GAP = 56;
const H_GAP = 32;
const ORIGIN_X = 120;

function sortStages(stages) {
  if (!Array.isArray(stages)) return [];
  return stages.slice().sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
}

/**
 * Assign top-to-bottom positions to nodes based on graph depth (BFS from start).
 * @param {import('@xyflow/react').Node[]} nodes
 * @param {import('@xyflow/react').Edge[]} edges
 */
export function applyVerticalLayout(nodes, edges) {
  if (!nodes.length) return nodes;

  const incoming = new Map();
  const outgoing = new Map();
  for (const n of nodes) {
    incoming.set(n.id, []);
    outgoing.set(n.id, []);
  }
  for (const e of edges) {
    if (incoming.has(e.target)) incoming.get(e.target).push(e.source);
    if (outgoing.has(e.source)) outgoing.get(e.source).push(e.target);
  }

  const startId = nodes.find((n) => n.type === 'bpmStart')?.id || nodes[0].id;
  const depth = new Map([[startId, 0]]);
  const queue = [startId];
  while (queue.length) {
    const id = queue.shift();
    const d = depth.get(id);
    for (const tid of outgoing.get(id) || []) {
      if (!depth.has(tid)) {
        depth.set(tid, d + 1);
        queue.push(tid);
      }
    }
  }

  const layers = new Map();
  for (const n of nodes) {
    const d = depth.get(n.id) ?? 0;
    if (!layers.has(d)) layers.set(d, []);
    layers.get(d).push(n.id);
  }

  const positioned = nodes.map((n) => ({ ...n }));
  const posById = new Map(positioned.map((n) => [n.id, n]));

  for (const [layerDepth, ids] of [...layers.entries()].sort((a, b) => a[0] - b[0])) {
    const count = ids.length;
    const totalW = count * NODE_W + (count - 1) * H_GAP;
    const startX = ORIGIN_X - totalW / 2 + NODE_W / 2;

    ids.forEach((id, i) => {
      const node = posById.get(id);
      if (!node) return;
      node.position = {
        x: startX + i * (NODE_W + H_GAP),
        y: layerDepth * (NODE_H + V_GAP),
      };
    });
  }

  return positioned;
}

/**
 * @param {object} design — BPM design with stages[] and optional elseAction
 * @returns {{ nodes: import('@xyflow/react').Node[], edges: import('@xyflow/react').Edge[] }}
 */
export function designToVerticalFlow(design) {
  const stages = sortStages(design?.stages);
  const nodes = [];
  const edges = [];

  nodes.push({
    id: 'bpm-start',
    type: 'bpmStart',
    position: { x: 0, y: 0 },
    data: { label: design?.transactionHint || 'Start' },
    draggable: false,
    selectable: false,
  });

  let prevIds = ['bpm-start'];

  stages.forEach((stage, si) => {
    const order = Number(stage.order) || si + 1;
    const routing =
      stage.routing === 'parallel'
        ? 'parallel'
        : stage.routing === 'autoApprove'
          ? 'autoApprove'
          : 'sequential';
    const approvers = Array.isArray(stage.approvers) ? stage.approvers : [];
    const stageNodeIds = [];

    if (routing === 'autoApprove') {
      const id = `stage-${order}-auto`;
      nodes.push({
        id,
        type: 'autoApprove',
        position: { x: 0, y: 0 },
        data: {
          kind: 'autoApprove',
          ref: 'n/a',
          stageOrder: order,
          routing: 'autoApprove',
          label: stage.label || 'Auto-approve',
        },
      });
      for (const sourceId of prevIds) {
        edges.push({
          id: `e-${sourceId}-${id}`,
          source: sourceId,
          target: id,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#059669', strokeWidth: 2 },
        });
      }
      prevIds = [id];
      return;
    }

    if (approvers.length === 0) {
      const id = `stage-${order}-empty`;
      nodes.push({
        id,
        type: 'singleApprover',
        position: { x: 0, y: 0 },
        data: {
          kind: 'lineManager',
          ref: 'n/a',
          stageOrder: order,
          routing,
          label: stage.label || 'Approver (configure)',
        },
      });
      stageNodeIds.push(id);
    } else {
      approvers.forEach((approver, ai) => {
        const id = `stage-${order}-app-${ai}`;
        const nodeType = kindToNodeType(approver.kind);
        nodes.push({
          id,
          type: nodeType,
          position: { x: 0, y: 0 },
          data: {
            kind: approver.kind || nodeTypeToDefaultKind(nodeType),
            ref: approver.ref ?? 'n/a',
            stageOrder: order,
            routing,
            label: approverDisplayLabel(approver),
            stageLabel: stage.label,
          },
        });
        stageNodeIds.push(id);
      });
    }

    for (const targetId of stageNodeIds) {
      for (const sourceId of prevIds) {
        edges.push({
          id: `e-${sourceId}-${targetId}`,
          source: sourceId,
          target: targetId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#0891b2', strokeWidth: 2 },
        });
      }
    }
    prevIds = stageNodeIds;
  });

  nodes.push({
    id: 'bpm-end',
    type: 'bpmEnd',
    position: { x: 0, y: 0 },
    data: { label: 'Complete' },
    draggable: false,
    selectable: false,
  });

  for (const sourceId of prevIds) {
    edges.push({
      id: `e-${sourceId}-bpm-end`,
      source: sourceId,
      target: 'bpm-end',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#059669', strokeWidth: 2 },
    });
  }

  const laidOut = applyVerticalLayout(nodes, edges);
  return { nodes: laidOut, edges };
}
